/**
 * Nameplate OCR Service (transplanted from onboarding app)
 *
 * Handles image capture and analysis of motor nameplates
 * using Claude Vision API via Lambda.
 */

const NAMEPLATE_API_URL = 'https://vqd9drk7sb.execute-api.us-east-1.amazonaws.com/prod/analyze';

async function compressImage(file, maxWidth = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const base64 = canvas.toDataURL('image/jpeg', quality).split(',')[1];
        resolve({ base64, mediaType: 'image/jpeg' });
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function analyzeWithBase64(base64, mediaType) {
  const response = await fetch(NAMEPLATE_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ imageBase64: base64, mediaType }),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API error: ${response.status} - ${errorText}`);
  }
  const result = await response.json();
  if (!result.success) throw new Error(result.error || 'Analysis failed');
  return result;
}

export async function analyzeNameplate(imageFile) {
  try {
    const { base64, mediaType } = await compressImage(imageFile);
    const sizeInMB = (base64.length * 0.75) / (1024 * 1024);
    if (sizeInMB > 5) {
      const { base64: smallerBase64, mediaType: smallerType } = await compressImage(imageFile, 1200, 0.7);
      if ((smallerBase64.length * 0.75) / (1024 * 1024) > 5) {
        throw new Error('Image too large even after compression.');
      }
      return analyzeWithBase64(smallerBase64, smallerType);
    }
    return analyzeWithBase64(base64, mediaType);
  } catch (error) {
    console.error('Nameplate analysis error:', error);
    return { success: false, error: error.message || 'Failed to analyze nameplate' };
  }
}

export function mapToScreeningFields(extractedData) {
  if (!extractedData) return {};
  return {
    hp: extractedData.hp || '',
    rpm: extractedData.rpm || '',
    frame: extractedData.frame || '',
    voltage: extractedData.voltage || '',
    manufacturer: extractedData.manufacturer || '',
    serialNumber: extractedData.serialNumber || '',
    confidence: extractedData.confidence || 'unknown',
  };
}
